<script setup lang="ts">
  import { ref } from "vue";
  import moment from "moment";
  import { Pencil, Plus, Trash2 } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import { Switch } from "@/components/ui/switch";
  import { Tooltip } from "@/components/ui/tooltip";
  import { ConfirmDialog } from "@/components/episto-ui/confirm-dialog";
  import ScriptingGuidelineDialog from "./ScriptingGuidelineDialog.vue";
  import { useScriptingGuidelinesStore } from "@stores/scriptingGuidelines";
  import { useToastsStore } from "@stores/toastsStore";
  import { useConfirmDelete } from "@/utils/composables/useConfirmDelete";
  import type { ScriptingGuideline } from "@/types";

  defineProps<{ guidelines: ScriptingGuideline[]; accountId: number | null; title: string; emptyMessage: string }>();

  const store = useScriptingGuidelinesStore();
  const toast = useToastsStore();

  const dialogOpen = ref(false);
  const editing = ref<ScriptingGuideline | null>(null);
  const openDialog = (guideline: ScriptingGuideline | null) => {
    editing.value = guideline;
    dialogOpen.value = true;
  };

  const toggle = (guideline: ScriptingGuideline, active: boolean) =>
    store.update(guideline, { active }).catch(() => toast.error("The rule could not be updated"));

  const {
    isOpen: isDeleteOpen,
    requestDelete,
    confirm: confirmDelete,
  } = useConfirmDelete<ScriptingGuideline>((guideline) =>
    store.destroy(guideline).catch(() => toast.error("The rule could not be deleted")),
  );
</script>

<template>
  <div>
    <div class="mb-3 flex items-center justify-between">
      <p class="heading-label text-muted-foreground">{{ title }}</p>
      <Button
        size="sm"
        variant="outline"
        @click="openDialog(null)">
        <Plus class="size-3.5" />Add a rule
      </Button>
    </div>
    <ul class="divide-y rounded-lg border">
      <li
        v-for="guideline in guidelines"
        :key="guideline.id"
        class="group flex items-start gap-3 px-4 py-3"
        :class="!guideline.active && 'opacity-60'">
        <Tooltip :content="guideline.active ? 'Active' : 'Inactive'">
          <span class="mt-0.5 inline-flex">
            <Switch
              size="sm"
              :model-value="guideline.active"
              @update:model-value="(value) => toggle(guideline, value)" />
          </span>
        </Tooltip>
        <div class="min-w-0 flex-1">
          <p class="text-sm font-medium text-foreground">{{ guideline.title }}</p>
          <p class="text-sm text-muted-foreground">{{ guideline.body }}</p>
          <p
            v-if="guideline.updated_by"
            class="mt-1 text-3xs text-muted-foreground">
            Edited by {{ guideline.updated_by }} · {{ moment(guideline.updated_at).format("YYYY-MM-DD") }}
          </p>
        </div>
        <div class="flex shrink-0 items-center gap-1 opacity-0 group-hover:opacity-100">
          <Tooltip content="Edit">
            <Button
              variant="ghost"
              size="icon-sm"
              class="text-muted-foreground"
              @click="openDialog(guideline)">
              <Pencil class="size-4" />
            </Button>
          </Tooltip>
          <Tooltip content="Delete">
            <Button
              variant="ghost"
              size="icon-sm"
              class="text-muted-foreground hover:text-status-error-strong"
              @click="requestDelete(guideline)">
              <Trash2 class="size-4" />
            </Button>
          </Tooltip>
        </div>
      </li>
      <li
        v-if="!store.loading && !guidelines.length"
        class="px-4 py-6 text-center text-sm text-muted-foreground">
        {{ emptyMessage }}
      </li>
    </ul>

    <ScriptingGuidelineDialog
      v-model:open="dialogOpen"
      :guideline="editing"
      :account-id="accountId" />

    <ConfirmDialog
      v-model:open="isDeleteOpen"
      title="Delete this rule?"
      description="The AI stops following it."
      confirm-label="Delete"
      @confirm="confirmDelete" />
  </div>
</template>
